/**
 * useDashboardLayoutSave (issue #451).
 *
 * Returns a `saveDashboardLayout` callback that persists a `DashboardLayout`
 * via the REST `PUT /api/v1/user/preferences` with an optimistic update: the
 * new layout is applied to the `preferencesSlice` immediately (before the
 * network call resolves), then on success the optimistic value is confirmed,
 * or on failure it is rolled back to the pre-save layout and an error is
 * recorded — so the grid visually reverts rather than keeping a layout the
 * server never persisted. Rethrows on failure so the caller (the dashboard
 * route) can react (stay in edit mode, show an inline error).
 *
 * Kept out of `preferencesSlice.ts` deliberately: `lib/apiClient.ts` imports
 * the `store` singleton to read the auth token, and the slice is part of the
 * store's reducer graph — importing apiClient there would create a circular
 * import (store -> rootReducer -> preferencesSlice -> apiClient -> store).
 */

import { useCallback } from 'react';
import { updateUserPreferences } from '../lib/apiClient';
import { useAppDispatch } from '../store';
import {
  dashboardLayoutSaveStarted,
  dashboardLayoutSaveSucceeded,
  dashboardLayoutSaveFailed,
} from '../store/features/preferences/preferencesSlice';
import type { DashboardLayout } from '../types/preferences';

export function useDashboardLayoutSave(): (layout: DashboardLayout) => Promise<void> {
  const dispatch = useAppDispatch();

  return useCallback(
    async (layout: DashboardLayout): Promise<void> => {
      dispatch(dashboardLayoutSaveStarted(layout));
      try {
        await updateUserPreferences({ dashboardLayout: layout });
        dispatch(dashboardLayoutSaveSucceeded());
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not save dashboard layout';
        dispatch(dashboardLayoutSaveFailed({ message }));
        throw err;
      }
    },
    [dispatch]
  );
}
